/**
 * What else keeps a "Public network listing" charger off the driver map, or
 * stops other drivers starting it (Warren, 22 Oct 2026: he turned the listing
 * on for his home charger and it never appeared). The driver map shows a
 * charger only when it is active, listed, and its Location is active, shown
 * on the driver map and has coordinates (apps/api routes/locations driver map
 * query); a driver from another organization can start it only when it is
 * open to guests with a guest price (packages/billing canGuestUseCharger).
 * Pure, so the form can list each missing step and a test can pin them.
 */

export interface MapListingFacts {
  listed: boolean;
  chargerActive: boolean;
  openToGuests: boolean;
  /** Empty when the guest price box is empty (unpriced = closed to guests). */
  guestPriceSet: boolean;
  /** Set when the charger is hidden from the driver map until it next
   *  connects (map_hidden_until_contact_after, ISO). */
  hiddenUntilContactSince?: string | null;
  location?: {
    name?: string;
    type?: string;
    is_active?: boolean;
    is_visible_on_driver_map?: boolean;
    address?: { coordinates?: { lat?: number | null; lng?: number | null } | null } | null;
  } | null;
}

/** One sentence per missing step, empty when the listing will show and start. */
export function mapListingBlockers(f: MapListingFacts): string[] {
  if (!f.listed) return [];
  const out: string[] = [];
  const loc = f.location;
  if (!f.chargerActive) out.push('The charger is inactive. Turn on Active.');
  if (f.hiddenUntilContactSince) {
    out.push(`Hidden from the driver map since ${f.hiddenUntilContactSince.slice(0, 10)} because it has not connected. It reappears on its own within 5 minutes of connecting.`);
  }
  if (loc) {
    const where = loc.name ? `"${loc.name}"` : 'its location';
    if (loc.is_active === false) out.push(`The location ${where} is inactive.`);
    if (loc.is_visible_on_driver_map === false) {
      out.push(loc.type === 'residential'
        ? `The location ${where} is a home, and homes are hidden from the driver map. Turn on "Show on driver map" for the location to list it.`
        : `The location ${where} is hidden from the driver map. Turn on "Show on driver map" for the location.`);
    }
    const c = loc.address?.coordinates;
    if (!c || c.lat == null || c.lng == null) out.push(`The location ${where} has no map position. Add its address so it can be placed on the map.`);
  }
  if (!f.openToGuests) out.push('Drivers outside your organization cannot start it. Turn on "Open to guests".');
  else if (!f.guestPriceSet && loc?.type !== 'residential') out.push('Guests cannot start it without a guest price. Set the guest markup (0 is allowed).');
  return out;
}
